import { types } from 'mobx-state-tree'

import { getRootStore } from '#state/models/getRootStore'
import { extractTsRestSuccess } from '#util/extractTsRest'

import { RundownListingBase } from './ListingBase'

export const RundownListing = RundownListingBase.named('RundownListing')
  .props({
    modelType: types.frozen<'rundown'>('rundown'),
    isEditingName: false,
    pendingLocationRequest: false,
  })
  .views((self) => ({
    get path() {
      return `/rundowns/${self.id}`
    },
    get isSelected(): boolean {
      return (
        getRootStore(self).location.getPathParam('rundownId') ===
        String(self.id)
      )
    },
  }))
  // sync actions
  .actions((self) => ({
    setIsEditingName(value: boolean) {
      self.isEditingName = value
    },
    setName(value: string) {
      self.name = value
    },
    setPendingLocationRequest(value: boolean) {
      self.pendingLocationRequest = value
    },
    trackModified() {
      self.analytics.trackDocModified('rundown', self.id)
    },
  }))
  // async actions
  .actions((self) => ({
    async moveToTrash() {
      const { privateTrash, sharedTrash } = getRootStore(self).rootFolders
      const folderId = self.isPrivate ? privateTrash?.id : sharedTrash?.id
      if (!folderId) return

      await extractTsRestSuccess(
        self.scrapi.rundowns.moveRundown({
          params: { id: self.id },
          body: { folderId },
        }),
        200,
      )
      self.setFolderId(folderId)
    },

    async moveToFolder(folderId: string) {
      await extractTsRestSuccess(
        self.scrapi.rundowns.moveRundown({
          params: { id: self.id },
          body: { folderId },
        }),
        200,
      )
      self.setFolderId(folderId)
    },

    async rename(name: string) {
      const params = {
        params: { id: self.id },
        body: {
          name,
        },
      }
      await extractTsRestSuccess(
        self.scrapi.rundowns.updateRundown(params),
        200,
      )
      self.trackModified()
      self.setIsEditingName(false)
      self.setName(name)
      const { currentRundown } = getRootStore(self)
      if (currentRundown?.id === self.id) {
        currentRundown.setName(name)
      }
    },

    async getAssociatedScriptCount(): Promise<number> {
      const { rows } = await getRootStore(self).apiClient.getRundown(self.id)
      const scriptMap: { [key: string]: boolean } = {}
      rows.forEach(({ identityScriptId, linkedScriptId }) => {
        const scriptId = identityScriptId ?? linkedScriptId
        if (scriptId) {
          scriptMap[scriptId] = true
        }
      })
      return Object.keys(scriptMap).length
    },

    async duplicate(): Promise<{ id: number }> {
      await getRootStore(self).doDebug()
      const {
        body: { id },
      } = await extractTsRestSuccess(
        self.scrapi.rundowns.copyRundown({
          params: {
            id: self.id,
          },
          body: {
            name: `Copy of ${self.name}`,
          },
        }),
        200,
      )
      self.analytics.trackDocCreated({
        type: 'rundown',
        format: getRootStore(self).currentOrg?.rundownSchemaName ?? 'rundown',
        accessLevel: 'shared',
        from: 'duplicate',
      })
      return { id }
    },

    async clone(): Promise<{ id: number; folderId: string }> {
      await getRootStore(self).doDebug()
      const {
        body: { id, folderId },
      } = await extractTsRestSuccess(
        self.scrapi.rundowns.cloneRundown({
          params: { id: self.id },
          body: { prefix: `Copy of ` },
        }),
        200,
      )

      self.analytics.trackDocCreated({
        type: 'rundown',
        format: getRootStore(self).currentOrg?.rundownSchemaName ?? 'rundown',
        accessLevel: 'shared',
        from: 'clone',
      })
      // load the folder that was created along with its listings
      const folder = await getRootStore(self).loadFolderSummary(folderId)
      folder?.loadPageOfDocuments()

      return { id, folderId }
    },
  }))
